import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOrders } from '../services/api';
import { MATERIAL_NAME, ORDER_STATUS_LABEL, type Order, type OrderStatus } from '../types';
import { money } from '../utils/format';
import { useTableSort } from '../hooks/useTableSort';

const STATUS_CLASS: Record<OrderStatus, string> = {
  open: 'badge-open',
  in_progress: 'badge-progress',
  filled: 'badge-filled',
};

const FILTERS: Array<{ key: 'all' | OrderStatus; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'filled', label: 'Filled' },
];

// Bottom-left module: recent orders from different companies, with status.
export default function OrdersPanel() {
  const { data, isLoading } = useQuery({ queryKey: ['orders'], queryFn: () => getOrders(500) });
  const [filter, setFilter] = useState<'all' | OrderStatus>('all');
  const { onSort, sortRows, indicator } = useTableSort<Order>('created', 'desc');

  const orders = data?.orders ?? [];
  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, filled: 0 } as Record<OrderStatus, number>;
    for (const o of orders) c[o.status]++;
    return c;
  }, [orders]);

  const filtered = filter === 'all' ? orders : orders.filter((o) => o.status === filter);
  const shown = sortRows(filtered, {
    order: (o) => o.order_no,
    created: (o) => o.created_at,
    customer: (o) => o.customer_name,
    material: (o) => MATERIAL_NAME[o.material] ?? o.material,
    qty: (o) => o.quantity,
    total: (o) => o.delivered_total,
    status: (o) => o.status,
  });

  return (
    <div className="card home-module" style={{ gridArea: 'orders' }} data-tour="orders">
      <div className="home-module-header">
        <span>Orders</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`btn btn-sm ${filter === f.key ? 'btn-primary' : ''}`}
              style={{ padding: '2px 9px' }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key !== 'all' && (
                <span style={{ opacity: 0.7, marginLeft: 4 }}>{counts[f.key]}</span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="home-module-body">
        {isLoading ? (
          <p className="muted" style={{ padding: 10 }}>
            Loading…
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => onSort('order')}>
                  Order{indicator('order')}
                </th>
                <th className="sortable" onClick={() => onSort('customer')}>
                  Customer{indicator('customer')}
                </th>
                <th className="sortable" onClick={() => onSort('material')}>
                  Material{indicator('material')}
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('qty')}>
                  Qty{indicator('qty')}
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('total')}>
                  Total{indicator('total')}
                </th>
                <th className="sortable" onClick={() => onSort('status')}>
                  Status{indicator('status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => (
                <tr key={o.id}>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {o.order_no ?? o.id.slice(0, 8)}
                  </td>
                  <td>{o.customer_name ?? '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{MATERIAL_NAME[o.material] ?? o.material}</td>
                  <td style={{ textAlign: 'right' }}>{o.quantity}</td>
                  <td style={{ textAlign: 'right' }}>{money(o.delivered_total)}</td>
                  <td>
                    <span className={`badge ${STATUS_CLASS[o.status]}`}>
                      {ORDER_STATUS_LABEL[o.status]}
                    </span>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 10 }}>
                    No orders.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
